import type {
    AttributionType,
    PersonaType,
    PhoneNumberAttributedValue,
    EmailAddressAttributedValue,
} from '../../../service';
import {
    WellKnownNetworkNames,
    OtherKnownNetworkNames,
} from '../../framework/wellKnownNetworkNames';

/**
 * Retrieve the DisplayName for the @param source attribution.
 * Returns the first corresponding attribute among:
 *  - Entry in DisplayNamesArray
 *  - @param email
 *  - Phone number
 * @param personaType the full persona
 * @param source the source to retrieve displayName for
 * @param email the main email for this source
 */
export function getDisplayNameForSource(
    personaType: PersonaType,
    sourceId: string,
    email: string
): string {
    if (personaType.DisplayNamesArray) {
        const displayNameKeyValue = personaType.DisplayNamesArray.find(displayName => {
            return (
                displayName &&
                displayName.Attributions.some(attribution => attribution === sourceId)
            );
        });

        if (displayNameKeyValue) {
            return displayNameKeyValue.Value;
        }
    }

    return email || retrievePhoneNumberForSource(personaType, sourceId);
}

export function getPersonaCoinNameForSource(personaType: PersonaType, sourceId: string) {
    let firstName = null;
    let middleName = null;
    let lastName = null;

    if (personaType.GivenNamesArray) {
        const firstNameKeyValue = personaType.GivenNamesArray.find(name => {
            return (
                name?.Attributions &&
                name.Attributions.some(attribution => attribution === sourceId)
            );
        });

        if (firstNameKeyValue) {
            firstName = firstNameKeyValue.Value;
        }
    }

    if (personaType.MiddleNamesArray) {
        const middleNameKeyValue = personaType.MiddleNamesArray.find(name => {
            return (
                name?.Attributions &&
                name.Attributions.some(attribution => attribution === sourceId)
            );
        });

        if (middleNameKeyValue) {
            middleName = middleNameKeyValue.Value;
        }
    }

    if (personaType.SurnamesArray) {
        const lastNameKeyValue = personaType.SurnamesArray.find(name => {
            return (
                name?.Attributions &&
                name.Attributions.some(attribution => attribution === sourceId)
            );
        });

        if (lastNameKeyValue) {
            lastName = lastNameKeyValue.Value;
        }
    }

    if (firstName || lastName) {
        return firstName + (middleName ? ' ' + middleName : '') + (lastName ? ' ' + lastName : '');
    }

    return getDisplayNameForSource(personaType, sourceId, personaType.EmailAddress?.EmailAddress);
}

/**
 * Searches for an email corresponding to the sourceId attribution in Emails1Array, Emails2Array, Emails3Array
 * Returns the first email found in those arrays, scanned in that order
 * @param persona The PersonaType to search emails in
 * @param sourceId The sourceId of the attribution
 */
export function retrieveEmailAddressForSource(personaType: PersonaType, sourceId: string): string {
    const emailsFound = [
        personaType.Emails1Array,
        personaType.Emails2Array,
        personaType.Emails3Array,
    ].map(array => retrieveFirstEmailForAttribution(array, sourceId));

    return emailsFound.find(email => !!email);
}

/**
 * Returns the first email with the corresponding attribution sourceId in the given array.
 * Returns undefined if no array is passed, or no email is found
 * @param attributedEmails array of AttributedEmails
 * @param sourceId sourceId of the attribution
 */
function retrieveFirstEmailForAttribution(
    attributedEmails: EmailAddressAttributedValue[],
    sourceId: string
): string {
    if (!attributedEmails) {
        return undefined;
    }

    const email = attributedEmails.find(attributedEmail =>
        attributedEmail.Attributions.some(source => source === sourceId)
    );
    return (
        email?.Value &&
        (email.Value.EmailAddress || email.Value.OriginalDisplayName || email.Value.Name)
    );
}

/**
 * Searches for a phone number corresponding to the sourceId attribution in the arrays storing phone numbers
 * Returns the first number found in those arrays
 * @param persona The PersonaType to search numbers in
 * @param sourceId The sourceId of the attribution
 */
export function retrievePhoneNumberForSource(personaType: PersonaType, sourceId: string): string {
    const phonesFound = [
        personaType.MobilePhonesArray,
        personaType.MobilePhones2Array,
        personaType.BusinessPhoneNumbersArray,
        personaType.BusinessPhoneNumbers2Array,
        personaType.HomePhonesArray,
        personaType.HomePhones2Array,
    ].map(array => retrieveFirstPhoneNumberForAttribution(array, sourceId));

    return phonesFound.find(phone => !!phone);
}

/**
 * Returns the first phone number with the corresponding attribution sourceId in the given array.
 * Returns undefined if no array is passed, or no number is found
 * @param attributedPhoneNumbers array of attributedPhoneNumbers
 * @param sourceId sourceId of the attribution
 */
function retrieveFirstPhoneNumberForAttribution(
    attributedPhoneNumbers: PhoneNumberAttributedValue[],
    sourceId: string
): string {
    if (!attributedPhoneNumbers) {
        return undefined;
    }

    const phone = attributedPhoneNumbers.find(phone =>
        phone.Attributions.some(source => source === sourceId)
    );
    return phone?.Value?.Number;
}

export function getLinkedContactSource(sourceDisplayName: string): string {
    // Converting special cases to WellKnownNetworkNames
    // depot/Utah/sources/dev/services/src/OData/Model/Providers/PersonProvider.cs
    // depot/Utah/sources/dev/relevance/src/People/NewPeopleQuery/DataSource/PersonSourceName.cs
    switch (sourceDisplayName) {
        case 'Outlook Contacts':
            return WellKnownNetworkNames.Outlook;
        case OtherKnownNetworkNames.CommunicationHistory:
            return WellKnownNetworkNames.RecipientCache;
        default:
            return sourceDisplayName;
    }
}

export function shouldShowAttribution(attribution: AttributionType): boolean {
    return (
        !attribution.IsHidden &&
        attribution.DisplayName !== WellKnownNetworkNames.RecipientCache &&
        attribution.DisplayName !== OtherKnownNetworkNames.CommunicationHistory
    );
}
